import { Injectable } from '@angular/core';
import type { Worker as TesseractWorker } from 'tesseract.js';
import { FrontSideResult, IdentityOcrResult, MrzTd1Result, MrzTd3Result } from './identity-ocr.models';
import { mergeIdentityOcr, mergePassportOcr } from './identity-ocr.merge';
import { findTd1Lines, findTd3Lines, matchMrzNames, parseTd1, parseTd3 } from './mrz-parser';
import { parseFrontSide } from './front-side-parser';
import { isOcrSupportedFile, loadImage, prepareForOcr } from './image-preprocess';

export interface IdentityOcrProgress {
  /** Ekranda gösterilecek adım metni. */
  step: string;
  /** 0-1, adımın içindeki ilerleme. */
  progress: number;
}

export class IdentityOcrCancelledError extends Error {
  constructor() {
    super('OCR iptal edildi.');
  }
}

export class IdentityOcrTimeoutError extends Error {
  constructor(step: string) {
    super(`OCR zaman aşımı: ${step}`);
  }
}

/** Worker + çekirdek + dil verisi ilk kez indirilirken yavaş bağlantıya pay. */
const WORKER_TIMEOUT_MS = 90_000;
/** Tek görselin okunması (spike: 1,5-5 sn). */
const RECOGNIZE_TIMEOUT_MS = 45_000;

/**
 * T.C. Kimlik Kartı'nı tarayıcıda Tesseract.js ile okur (masak-kimlik-ocr.md §3-4). Görsel ve OCR
 * metni tarayıcıdan ÇIKMAZ; Tesseract çekirdeği, worker ve dil verisi CDN'den değil uygulamanın
 * kendi assets/ocr klasöründen yüklenir (angular.json). Kütüphane ana pakete girmez — ilk okumada
 * dinamik import ile yüklenir. Worker'lar her okumadan sonra kapatılır.
 *
 * Aynı anda tek okuma: yeni okuma ya da cancel() öncekini keser (önceki IdentityOcrCancelledError atar).
 * Her adım zaman aşımına bağlı: Tesseract worker içinde çökerse (WASM/bellek) bekleyen işi
 * reddetmiyor — okuma sonsuza dek "sürüyor" kalıp modalı kilitliyordu.
 */
@Injectable({ providedIn: 'root' })
export class IdentityOcrService {
  private runId = 0;
  private workers: TesseractWorker[] = [];

  async read(
    front: File | undefined,
    back: File | undefined,
    onProgress?: (p: IdentityOcrProgress) => void,
  ): Promise<IdentityOcrResult> {
    await this.cancel();
    const run = this.runId;
    const check = () => {
      if (run !== this.runId) throw new IdentityOcrCancelledError();
    };
    const report = (step: string, progress = 0) => {
      if (run === this.runId) onProgress?.({ step, progress });
    };
    const useFront = isOcrSupportedFile(front) ? front : undefined;
    const useBack = isOcrSupportedFile(back) ? back : undefined;

    try {
      report('OCR bileşenleri yükleniyor…');
      const tesseract = await this.loadTesseract();
      check();

      let mrz: MrzTd1Result | null = null;
      if (useBack) {
        const worker = await this.createWorker(tesseract, 'eng', run, (p) => report('Arka yüz okunuyor…', p));
        await this.withTimeout(worker.setParameters({
          tessedit_char_whitelist: 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789<',
          tessedit_pageseg_mode: '3' as any, // PSM.AUTO — spike'ta MRZ'yi tam görselde bulan kip
        }), RECOGNIZE_TIMEOUT_MS, 'arka yüz ayarı');
        const img = await loadImage(useBack);
        // Fotoğraf ters ya da yan çekilmiş olabilir: MRZ bulunana kadar döndürerek dene.
        for (const rotation of [0, 180, 90, 270] as const) {
          check();
          const { data } = await this.withTimeout(worker.recognize(prepareForOcr(img, rotation)), RECOGNIZE_TIMEOUT_MS, 'arka yüz');
          const lines = findTd1Lines(data.text);
          if (lines) {
            mrz = parseTd1(lines);
            break;
          }
        }
        await this.terminate(worker);
      }

      let frontSide: FrontSideResult | null = null;
      if (useFront) {
        check();
        const worker = await this.createWorker(tesseract, 'tur', run, (p) => report('Ön yüz okunuyor…', p));
        const img = await loadImage(useFront);
        // Ön yüzde renkli görsel gri tondan iyi ve hızlı okuyor (tarayıcı denemesi: soyad ve doğum
        // tarihi yalnızca renkliyle çıktı); gri ton yedek.
        const attempts: [0 | 180, boolean][] = [[0, false], [180, false], [0, true]];
        for (const [rotation, grayscale] of attempts) {
          check();
          const { data } = await this.withTimeout(
            worker.recognize(prepareForOcr(img, rotation, grayscale)), RECOGNIZE_TIMEOUT_MS, 'ön yüz');
          frontSide = parseFrontSide(data.text);
          if (frontSide.tckn || frontSide.serialNumber) break;
        }
        await this.terminate(worker);
      }

      check();
      return mergeIdentityOcr(mrz, frontSide);
    } finally {
      if (run === this.runId) await this.terminateAll();
    }
  }

  /**
   * Pasaportun fotoğraflı sayfasını okur: önce MRZ (TD3) İngilizce/beyaz listeyle, bulunduğu dönüşle
   * sayfa bir de Türkçe okunur — ad Türkçe karakterleriyle MRZ'ye oturtulur (mergePassportOcr).
   */
  async readPassport(page: File | undefined, onProgress?: (p: IdentityOcrProgress) => void): Promise<IdentityOcrResult> {
    await this.cancel();
    const run = this.runId;
    const check = () => {
      if (run !== this.runId) throw new IdentityOcrCancelledError();
    };
    const report = (step: string, progress = 0) => {
      if (run === this.runId) onProgress?.({ step, progress });
    };
    const usePage = isOcrSupportedFile(page) ? page : undefined;

    try {
      report('OCR bileşenleri yükleniyor…');
      const tesseract = await this.loadTesseract();
      check();

      let mrz: MrzTd3Result | null = null;
      let pageSide: FrontSideResult | null = null;
      if (usePage) {
        const img = await loadImage(usePage);
        const eng = await this.createWorker(tesseract, 'eng', run, (p) => report('MRZ okunuyor…', p));
        await this.withTimeout(eng.setParameters({
          tessedit_char_whitelist: 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789<',
          tessedit_pageseg_mode: '3' as any,
        }), RECOGNIZE_TIMEOUT_MS, 'MRZ ayarı');
        // MRZ sayfanın altında: önce alt %45 (yoğun sayfada otomatik bölütleme 2. satırı kaçırıyordu;
        // kırpılınca hem doğru hem hızlı), olmazsa tam sayfa; ters/yan çekilmişse döndürerek.
        const attempts: [0 | 90 | 180 | 270, number][] = [[0, 0.45], [0, 1], [180, 0.45], [180, 1], [90, 0.45], [270, 0.45]];
        let foundRotation: 0 | 90 | 180 | 270 = 0;
        for (const [rotation, keepBottom] of attempts) {
          check();
          const { data } = await this.withTimeout(
            eng.recognize(prepareForOcr(img, rotation, true, keepBottom)), RECOGNIZE_TIMEOUT_MS, 'MRZ');
          const lines = findTd3Lines(data.text);
          if (lines) {
            mrz = parseTd3(lines);
            foundRotation = rotation;
            break;
          }
        }
        await this.terminate(eng);

        check();
        const tur = await this.createWorker(tesseract, 'tur', run, (p) => report('Sayfa okunuyor…', p));
        for (const grayscale of [false, true]) {
          check();
          const { data } = await this.withTimeout(
            tur.recognize(prepareForOcr(img, foundRotation, grayscale)), RECOGNIZE_TIMEOUT_MS, 'pasaport sayfası');
          pageSide = parseFrontSide(data.text);
          // Ad MRZ'ye oturduysa yeter; oturmadıysa gri tonla bir kez daha.
          if (!mrz || matchMrzNames(pageSide.upperWords, mrz.nameLine)) break;
        }
        await this.terminate(tur);
      }

      check();
      return mergePassportOcr(mrz, pageSide);
    } finally {
      if (run === this.runId) await this.terminateAll();
    }
  }

  /** Süren okumayı keser (modal kapanınca, yeni dosya seçilince, "İptal") — açık worker'lar kapatılır. */
  async cancel(): Promise<void> {
    this.runId++;
    await this.terminateAll();
  }

  private async loadTesseract(): Promise<any> {
    const mod: any = await import('tesseract.js');
    return mod.createWorker ? mod : mod.default;
  }

  private async createWorker(
    tesseract: any,
    lang: 'eng' | 'tur',
    run: number,
    onProgress: (p: number) => void,
  ): Promise<TesseractWorker> {
    const base = new URL('assets/ocr/', document.baseURI).href;
    const creating: Promise<TesseractWorker> = tesseract.createWorker(lang, 1 /* OEM.LSTM_ONLY */, {
      workerPath: `${base}worker.min.js`,
      corePath: `${base}core`,
      langPath: `${base}lang`,
      gzip: true,
      workerBlobURL: false,
      // Dil verisi zaten aynı sunucudan, tarayıcı HTTP önbelleğiyle geliyor; IndexedDB'ye 5 MB'lık
      // ikinci kopya yazılmasın (yarım kalan yazım sonraki açılışı bozabiliyor).
      cacheMethod: 'none',
      // Varsayılan davranış hatayı ana iş parçacığında yakalanmamış olarak fırlatmak.
      errorHandler: () => undefined,
      logger: (m: { status: string; progress: number }) => {
        if (m.status === 'recognizing text') onProgress(m.progress);
      },
    });
    let worker: TesseractWorker;
    try {
      worker = await this.withTimeout(creating, WORKER_TIMEOUT_MS, 'OCR bileşenleri');
    } catch (e) {
      // Zaman aşımında worker sonradan oluşursa sahipsiz kalmasın.
      creating.then((w) => w.terminate()).catch(() => undefined);
      throw e;
    }
    // Oluşturulurken okuma kesildiyse worker'ı hemen kapat — listeye girip açık kalmasın.
    if (run !== this.runId) {
      await worker.terminate().catch(() => undefined);
      throw new IdentityOcrCancelledError();
    }
    this.workers.push(worker);
    return worker;
  }

  private withTimeout<T>(promise: Promise<T>, ms: number, step: string): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      const timer = setTimeout(() => reject(new IdentityOcrTimeoutError(step)), ms);
      promise.then(
        (value) => {
          clearTimeout(timer);
          resolve(value);
        },
        (error) => {
          clearTimeout(timer);
          reject(error);
        },
      );
    });
  }

  private async terminate(worker: TesseractWorker) {
    this.workers = this.workers.filter((w) => w !== worker);
    await worker.terminate().catch(() => undefined);
  }

  private async terminateAll() {
    const workers = this.workers;
    this.workers = [];
    await Promise.all(workers.map((w) => w.terminate().catch(() => undefined)));
  }
}
